import { useCallback, useEffect, useState } from "react";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import baseUrl from "../../../api/baseUrl";
import {
  purchaseStatusBadgeClass,
  purchaseStatusLabel,
} from "../../../utils/sms/purchaseStatus";
import { PanelCard, SectionHeader } from "../components/accountUi";

type PurchaseRow = {
  _id: string;
  packageName: string;
  tokens: number;
  amount: number;
  status: string;
  statusMessage?: string | null;
  paidAt?: string;
  createdAt: string;
};

type GrantRow = {
  _id: string;
  tokens: number;
  note?: string;
  createdAt: string;
};

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

/** WhatsApp / email purchase history and free grants, same tables as the SMS tab. */
export default function TokenHistoryPanel({ channel }: { channel: "whatsapp" | "email" }) {
  const label = channel === "whatsapp" ? "WhatsApp" : "email";
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [grants, setGrants] = useState<GrantRow[]>([]);

  const load = useCallback(() => {
    const params = { channel };
    baseUrl
      .get("/client/sms-packages/purchases", { params })
      .then((res) => setPurchases(res?.data?.success ? res.data.data ?? [] : []))
      .catch(() => setPurchases([]));
    baseUrl
      .get("/client/sms-packages/grants", { params })
      .then((res) => setGrants(res?.data?.success ? res.data.data ?? [] : []))
      .catch(() => setGrants([]));
  }, [channel]);

  useEffect(() => {
    load();
    window.addEventListener("sms-balance-changed", load);
    return () => window.removeEventListener("sms-balance-changed", load);
  }, [load]);

  if (purchases.length === 0 && grants.length === 0) return null;

  return (
    <>
      {purchases.length > 0 ? (
        <PanelCard className="overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
            <SectionHeader
              icon={ShoppingCartOutlinedIcon}
              title="Purchase history"
              subtitle={`Your ${label} package orders`}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/80 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Package</th>
                  <th className="px-4 py-3">Tokens</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((row) => (
                  <tr key={row._id} className="border-t border-slate-100 dark:border-slate-700/80">
                    <td className="px-4 py-3">{formatDate(row.paidAt || row.createdAt)}</td>
                    <td className="px-4 py-3">{row.packageName}</td>
                    <td className="px-4 py-3">{row.tokens.toLocaleString()}</td>
                    <td className="px-4 py-3">Rs. {row.amount.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${purchaseStatusBadgeClass(row.status)}`}
                      >
                        {purchaseStatusLabel(row.status, row.statusMessage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PanelCard>
      ) : null}

      {grants.length > 0 ? (
        <PanelCard className="overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
            <SectionHeader
              icon={CardGiftcardOutlinedIcon}
              title="Free token grants"
              subtitle={`Complimentary ${label} tokens from your platform admin`}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/80 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Tokens</th>
                  <th className="px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((row) => (
                  <tr key={row._id} className="border-t border-slate-100 dark:border-slate-700/80">
                    <td className="px-4 py-3">{formatDate(row.createdAt)}</td>
                    <td className="px-4 py-3 font-medium text-emerald-700">
                      +{row.tokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">{row.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PanelCard>
      ) : null}
    </>
  );
}
